import { Injectable, OnDestroy } from '@angular/core';
import { SuggestionHttpService } from './suggestion.http.service';
import { objectToCamel } from 'ts-case-convert';
import { BehaviorSubject, lastValueFrom, Subscription, interval } from 'rxjs';
import { SuggestionType } from 'src/app/models/enums/suggestionType';
import { SuggestionSortType } from 'src/app/models/enums/suggestionSortType';
import { SuggestionPriority } from 'src/app/models/enums/suggestionPriority';
import { SuggestionStatus } from 'src/app/models/enums/suggestionStatus';
import { ISuggestion } from 'src/app/models/suggestion';
import { CommunicationUserType } from 'src/app/models/enums';

@Injectable({
  providedIn: 'root',
})
export class SuggestionService implements OnDestroy {
  private pollingSubscription: Subscription | null = null;
  private readonly POLLING_INTERVAL = 10 * 60 * 1000; // 10 minutes

  private pendingCountSubject = new BehaviorSubject<number>(0);
  public pendingCount$ = this.pendingCountSubject.asObservable();

  constructor(private suggestionHttpService: SuggestionHttpService) {}

  ngOnDestroy() {
    this.stopPolling();
  }

  startPolling() {
    this.fetchPendingSuggestionsCount();

    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    this.pollingSubscription = interval(this.POLLING_INTERVAL).subscribe(() => {
      this.fetchPendingSuggestionsCount();
    });
  }

  stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  async fetchPendingSuggestionsCount() {
    try {
      const source$ = this.suggestionHttpService.getPendingSuggestionsCount();
      const response = await lastValueFrom(source$);
      const count = (response as number) ?? 0;
      this.pendingCountSubject.next(count);
      return count;
    } catch (error) {
      console.error('Error fetching pending suggestions count:', error);
      return 0;
    }
  }

  getPendingCount() {
    return this.pendingCountSubject.value;
  }

  async createSuggestion(suggestion: ISuggestion) {
    const source$ = this.suggestionHttpService.createSuggestion(suggestion);
    const response = await lastValueFrom(source$);
    const suggestionCreated = objectToCamel(response as object);
    return suggestionCreated;
  }
  
  async updateSuggestion(suggestion: ISuggestion) {
    const source$ = this.suggestionHttpService.updateSuggestion(suggestion);
    const response = await lastValueFrom(source$);
    const suggestionUpdated = objectToCamel(response as object);
    this.fetchPendingSuggestionsCount();
    return suggestionUpdated;
  }
  
  async getSuggestions(userType: CommunicationUserType, page?: number, size?: number) {
    const source$ = this.suggestionHttpService.getSuggestions(
      userType,
      page,
      size,
      SuggestionSortType.CREATION
    );
    const response = await lastValueFrom(source$);
    const suggestions = objectToCamel(response as object);
    const suggestionsArray = this.getData(suggestions);
    return suggestionsArray;
  }
  
  async getSuggestionById(id: number) {
    const source$ = this.suggestionHttpService.getSuggestionById(id)
    const response = await lastValueFrom(source$);
    const suggestion = objectToCamel(response as object);
    return suggestion;
  }

  async filterSuggestions(
    communicationUserType: CommunicationUserType,
    page: number,
    size: number,
    sort: SuggestionSortType,
    clientsSelected: number[],
    townsSelected: number[],
    typesSelected: SuggestionType[],
    statusSelected: SuggestionStatus[],
    prioritiesSelected: SuggestionPriority[],
    searchValue?: string,
    servicesSelected?: number[]
  ) {
    const source$ = this.suggestionHttpService.filter(
      communicationUserType,
      page,
      size,
      sort,
      clientsSelected,
      townsSelected,
      typesSelected,
      statusSelected,
      prioritiesSelected,
      searchValue,
      servicesSelected
    );
    const response = await lastValueFrom(source$);
    const suggestions = objectToCamel(response as object);
    const suggestionsArray = this.getData(suggestions);
    return suggestionsArray;
  }

  async sendConversationMessage(suggestionId: number, newMessage: string) {
    const source$ = this.suggestionHttpService.sendConversationMessage(suggestionId, newMessage);
    const response = await lastValueFrom(source$);
    const updatedSuggestion = objectToCamel(response as object);
    return updatedSuggestion;
  }

  getData(response: any) {
    return response.data;
  }

}
